import { DndContext, DragEndEvent, type Modifier } from '@dnd-kit/core';
import React, { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QuizControls from '../QuizControls';
import { QuizProps } from '../QuizModal';
import AnswerDraggable from './AnswerDraggable';
import QuestionDroppable from './QuestionDroppable';
interface Question {
  id: string;
  text: string;
}

interface Answer {
  id: string;
  text: string;
}

interface AnswerMapping {
  [key: string]: string;
}

export interface MatchPairsQuiz {
  type: 'match_pairs';
  questions: Question[];
  answers: Answer[];
  answerMapping: AnswerMapping;
  times_in_video: number[];
  maxTries: number;
}

interface MatchPairsModalProps extends QuizProps {
  quiz: MatchPairsQuiz;
  submission?: Map<string, Answer>;
  setSubmission?: (submission: Map<string, Answer>) => void;
}

const MatchPairsModal = memo((props: MatchPairsModalProps) => {
  const [draggableToDroppableMapping, setDraggableToDroppableMapping] = React.useState<Map<string, Answer>>(
    props.submission || new Map<string, Answer>()
  );
  const [message, setMessage] = React.useState('');
  /*const [submitCount, setSubmitCount] = React.useState(0);*/
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const handleDragEnd = (event: DragEndEvent) => {
    const { over, active } = event;
    let newMap;
    if (over && active) {
      newMap = new Map(
        draggableToDroppableMapping.set(
          over.id as string,
          props.quiz.answers.find((answer) => answer.id === active.id) as Answer
        )
      );
      setDraggableToDroppableMapping(newMap);
      console.log(newMap);
    }
  };

  const handleReset = () => {
    setDraggableToDroppableMapping(new Map<string, Answer>());
    setMessage('');
    props.setCorrect(false);
    props.setSubmitted(false);
  };

  const handleSubmission = () => {
    setMessage('');
    if (draggableToDroppableMapping.size === props.quiz.questions.length) {
      props.setTriesCounter(props.triesCounter + 1);
      props.setSubmitted(true);
      props.setSubmission && props.setSubmission(draggableToDroppableMapping);
      let correct = true;
      draggableToDroppableMapping.forEach((value, key) => {
        const answerId = props.quiz.answerMapping[key];
        const answerText = props.quiz.answers.find((answer) => answer.id === answerId)?.text;
        if (answerText !== value.text) {
          correct = false;
        }
      });
      props.setCorrect(correct);
    } else {
      setMessage(t('match_pairs.all_pairs'));
    }
  };

  const showCorrect = () => {
    const correctAnswers = new Map<string, Answer>();
    props.quiz.questions.forEach((question) => {
      const answerId = props.quiz.answerMapping[question.id];
      correctAnswers.set(question.id, props.quiz.answers.find((answer) => answer.id === answerId) as Answer);
    });
    setDraggableToDroppableMapping(correctAnswers);
  };
  const restrictToModal: Modifier = ({ transform, draggingNodeRect }) => {
    const boundaryRect = modalRef.current?.getBoundingClientRect();
    const nodeRect = draggingNodeRect;
    if (!boundaryRect || !nodeRect) {
      return transform;
    }

    const { left: bL, right: bR, top: bT, bottom: bB } = boundaryRect;
    const { left: nL, right: nR, top: nT, bottom: nB } = nodeRect;

    const minX = bL - nL;
    const maxX = bR - nR;
    const minY = bT - nT;
    const maxY = bB - nB;

    const x = Math.min(Math.max(transform.x, minX), maxX);
    const y = Math.min(Math.max(transform.y, minY), maxY);

    return {
      ...transform,
      x,
      y,
    };
  };

  return (
    <div className="flex flex-col justify-between" ref={modalRef}>
      <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToModal]}>
        <div className="flex flex-col space-x-4">
          <div className="flex flex-col space-y-4">
            {props.quiz.questions.map((question) => (
              <QuestionDroppable
                key={question.id}
                id={question.id}
                text={question.text}
                draggedItem={draggableToDroppableMapping.get(question.id)?.text}
              />
            ))}
          </div>
          <div className="h-8"></div>
          <div className="flex flex-row flex-wrap gap-4 w-full justify-center">
            {props.quiz.answers.map((answer) => {
              for (const [key, value] of draggableToDroppableMapping) {
                if (value.id === answer.id) {
                  return null;
                }
              }
              return <AnswerDraggable key={answer.id} id={answer.id} text={answer.text} />;
            })}
          </div>
        </div>
      </DndContext>
      {message && (
        <div className="mt-4 text-danger">
          <p>{message}</p>
        </div>
      )}

      <QuizControls
        selectedQuiz={props.selectedQuiz}
        numberOfQuiz={props.numberOfQuiz}
        onClose={props.onClose}
        handleNextQuiz={props.handleNextQuiz}
        handlePreviousQuiz={props.handlePreviousQuiz}
        handleSubmission={handleSubmission}
        handleReset={handleReset}
        showCorrect={showCorrect}
        maxTries={props.quiz.maxTries}
        submitCount={props.triesCounter}
        correct={props.correct}
      />
    </div>
  );
});

export default MatchPairsModal;
